import React, { useState } from 'react';
import {
  autoUpdate,
  flip,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { builderSampledCohortSelector } from '../../../../../redux/selectors/builder';
import {
  builderSetCohortSamplingRate,
  CohortSampleContextType,
  IBuilderSampledCohortSamplingRate,
} from '../../../../../redux/slices/oldBuilder';
import BuilderInspectConfigMenu from './BuilderInspectConfigMenu';
import { UnreachableCaseError } from 'ts-essentials';
import { SingleSelectLabel } from '../../../../../components/Dropdown/SingleSelect';
import { FloatingLayer } from '../../../../../components/FloatingManager';

const getValueFromSamplingRate = (
  samplingRate: IBuilderSampledCohortSamplingRate,
) => {
  const { type, sampleAbsolute, samplePercentage } = samplingRate;

  if (type === CohortSampleContextType.Percentage) {
    return samplePercentage;
  } else if (type === CohortSampleContextType.Absolute) {
    return sampleAbsolute;
  } else {
    throw new UnreachableCaseError(type);
  }
};

const BuilderInspectConfig = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useAppDispatch();

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(2), flip()],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const builderSampledCohort = useAppSelector(builderSampledCohortSelector);
  const { samplingRate } = builderSampledCohort;

  const handleChangeConfig = (
    contextType: CohortSampleContextType,
    newValue: number | null,
  ) => {
    const value: IBuilderSampledCohortSamplingRate = {
      type: contextType,
      sampleAbsolute: null,
      samplePercentage: null,
    };

    if (contextType === CohortSampleContextType.Percentage) {
      value.samplePercentage = newValue;
    } else if (contextType === CohortSampleContextType.Absolute) {
      value.sampleAbsolute = newValue;
    }

    dispatch(
      builderSetCohortSamplingRate({
        value,
      }),
    );
    setIsOpen(false);
  };

  const currentValue = getValueFromSamplingRate(samplingRate);

  return (
    <>
      <SingleSelectLabel
        ref={refs.setReference}
        {...getReferenceProps()}
        isAlwaysActive={true}
      >
        {currentValue}
        {samplingRate.type === CohortSampleContextType.Percentage && '%'}
      </SingleSelectLabel>
      {isOpen && (
        <FloatingLayer>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <BuilderInspectConfigMenu
              sampleContextType={samplingRate.type}
              sampleValue={currentValue}
              onChange={handleChangeConfig}
            />
          </div>
        </FloatingLayer>
      )}
    </>
  );
};

export default BuilderInspectConfig;
